// PastDueBanner — sticky top, пока у ЛОКАЛЬНОЙ подписки статус `past_due`.
// Spec: docs/ui-spec/billing.past-due.banner.{desktop,mobile}.md
//
// Источник — реальная подписка (`billing.subscriptions.get`), а не сессия:
// прежняя версия читала `session.user.billing?.pastDueSince`, а поля `billing`
// в `SessionUser` нет — баннер работал только через `?demo=`.
//
// Почему баннер остаётся после переезда продажи в IEC ID: `past_due` бывает
// только у НАШЕЙ кассы, и у части людей локальная подписка ещё жива. У прав от
// экосистемы такого состояния нет вовсе (§4.7: отмена ничего не шлёт, право
// доживает до `expiresAt`), поэтому здесь читается именно локальная подписка.
//
// Отсчёт ведётся от конца оплаченного периода: именно тогда провайдер не смог
// списать, и с этого момента идёт grace-окно `PAST_DUE_GRACE_DAYS`.

import { PAST_DUE_GRACE_DAYS } from "@studyhub/shared/billing";
import { Button } from "@studyhub/ui/components/button";
import { IconAlertOctagon, IconX } from "@tabler/icons-react";
import { Link, useRouterState } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/cn";
import { api } from "@/lib/trpc-client";
import { useSessionStore } from "@/stores/session";

const DISMISS_KEY = "studyhub:past-due-banner:dismissed-at";

export function PastDueBanner() {
	const { t } = useTranslation("billing");
	const isAuthenticated = useSessionStore((s) => s.isAuthenticated);
	const pathname = useRouterState({ select: (s) => s.location.pathname });

	const subscription = api.billing.subscriptions.get.useQuery(undefined, {
		enabled: isAuthenticated,
		staleTime: 5 * 60_000,
	});
	const sub = subscription.data?.subscription ?? null;
	const isPastDue = sub?.status === "past_due";

	const [dismissed, setDismissed] = useState(false);

	useEffect(() => {
		if (typeof window === "undefined") return;
		const v = localStorage.getItem(DISMISS_KEY);
		if (v && Date.now() - Number(v) < 12 * 3600_000) setDismissed(true);
	}, []);

	// Скрыт на переходном экране оплаты (минимальный shell).
	if (pathname.startsWith("/billing/checkout")) return null;
	if (!isPastDue || !sub || dismissed) return null;

	const daysSince = Math.floor((Date.now() - Date.parse(sub.currentPeriodEnd)) / 86_400_000);
	const daysLeft = Math.max(0, PAST_DUE_GRACE_DAYS - daysSince);
	// Dismiss скрыт когда осталось <3 дней (критично).
	const canDismiss = daysLeft >= 3;

	const onDismiss = (): void => {
		if (typeof window !== "undefined") localStorage.setItem(DISMISS_KEY, String(Date.now()));
		setDismissed(true);
	};

	return (
		<div
			className={cn(
				"flex items-center gap-3 border-b border-destructive/30 bg-destructive/10 px-4 py-2.5 text-sm sm:px-6",
			)}
			role="alert"
			// См. `BannerStack`: первый баннер стопки получает padding-top не ниже
			// собственного (иначе `py-2.5` перезаписывался бы нулём).
			style={{ "--sh-banner-pt": "0.625rem" } as React.CSSProperties}
		>
			<IconAlertOctagon size={18} className="shrink-0 text-destructive" />
			<div className="min-w-0 flex-1">
				<div className="font-medium text-destructive">{t("pastDue.title")}</div>
				<div className="text-xs tabular-nums text-destructive/80">
					{t("pastDue.daysLeft", { count: daysLeft })}
				</div>
			</div>
			<Button
				asChild
				size="sm"
				variant="default"
				className="h-8 min-h-[44px] cursor-pointer sm:min-h-0"
			>
				<Link to="/settings/billing" search={{}}>
					{t("pastDue.updateCta")}
				</Link>
			</Button>
			{canDismiss && (
				<button
					type="button"
					onClick={onDismiss}
					className="cursor-pointer rounded-md p-1.5 text-destructive/70 transition-colors duration-fast hover:bg-destructive/10 hover:text-destructive"
					aria-label={t("pastDue.dismiss")}
				>
					<IconX size={16} />
				</button>
			)}
		</div>
	);
}
